import { msg } from "@lingui/core/macro";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { ClipboardCheck, FolderOpen, Trash2 } from "@fleet/icons";
import {
  addInventoryCountLineMutation,
  createInventoryCountMutation,
  deleteInventoryCountLineMutation,
  deleteInventoryCountMutation,
  fromMinorQty,
  getInventoryCountLinesQuery,
  getInventoryCountsQuery,
  getLocationsQuery,
  getLotsByProductQuery,
  getProductsQuery,
  isTracked,
  parseQtyInput,
  queryKeys,
  validateInventoryCountMutation,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { useResetOnOrgSwitch } from "@/features/inventory/use-reset-on-org-switch";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { EmptyOrdersIllustration } from "@/components/ui/illustrations";
import { Select } from "@/components/ui/select";
import { SwipeRow, SwipeTableRow } from "@/components/ui/swipe-row";
import { showToast } from "@/components/ui/toast";
import type {
  InventoryCountLineRecord,
  InventoryCountRecord,
  LocationRecord,
  LotRecord,
  ProductRecord,
} from "@stockpilot/powersync";

function CountsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();
  const search = useSearch({ from: "/_app/counts" });
  const navigate = useNavigate();

  const [locationId, setLocationId] = createSignal("");
  const [reference, setReference] = createSignal("");

  // The open count is deep-linkable: the URL (`?count=<id>`) is the source of
  // truth. Pasting/sharing `/counts?count=<id>` opens exactly that count;
  // reloading rebuilds it (idempotent). `setOpenCountId` only writes the URL.
  const openCountId = (): string | null => search().count ?? null;
  const setOpenCountId = (id: string | null) => {
    void navigate({
      to: "/counts",
      search: id ? { count: id } : {},
      replace: true,
    });
  };

  // Router deferred flush guard: if boot lost the `?count=` of the
  // initial deep link, reapply it synchronously from the snapshot.
  onMount(() => {
    const initialCount = takeInitialSearchParam("count", "/counts");
    if (initialCount && !openCountId()) {
      setOpenCountId(initialCount);
    }
  });

  // Multi-organization isolation: when switching companies, the open count
  // belongs to the previous org → discard the pointer.
  useResetOnOrgSwitch(() => setOpenCountId(null));

  const [lineProduct, setLineProduct] = createSignal("");
  const [lineLot, setLineLot] = createSignal("");
  const [lineQty, setLineQty] = createSignal("");

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const counts = createPowerSyncQuery<InventoryCountRecord>({
    queryKey: () => queryKeys.inventory.counts(orgId()),
    queryFn: () => getInventoryCountsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const openLines = createPowerSyncQuery<InventoryCountLineRecord>({
    queryKey: () => queryKeys.inventory.countLines(openCountId() ?? undefined),
    queryFn: () => getInventoryCountLinesQuery(drizzle()!, openCountId()!),
    db: () => db(),
    enabled: () => Boolean(drizzle() && openCountId()),
  });

  // Lots of the selected product in the line form (when tracked).
  const lineLots = createPowerSyncQuery<LotRecord>({
    queryKey: () => queryKeys.inventory.lotsByProduct(lineProduct() || undefined),
    queryFn: () => getLotsByProductQuery(drizzle()!, lineProduct()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && lineProduct()),
  });

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? id;
  });
  const lotName = createMemo(() => {
    const map = new Map((lineLots.data ?? []).map((l) => [l.id, l.name]));
    return (id: string) => map.get(id) ?? id;
  });
  const locationName = createMemo(() => {
    const map = new Map((locations.data ?? []).map((l) => [l.id, l.name]));
    return (id: string) => map.get(id) ?? id;
  });

  const selectedProduct = createMemo(() =>
    (products.data ?? []).find((p) => p.id === lineProduct()),
  );
  const productTracked = createMemo(() => isTracked(selectedProduct()?.tracking));

  const openCount = createMemo(() => (counts.data ?? []).find((c) => c.id === openCountId()));

  // Resolves the deep-linked count (`?count=<id>`): present / loading /
  // not-found / offline. Avoids the silent empty panel when the count does not
  // exist, was deleted, or the user lost access (PowerSync sync rules do not
  // sync down rows without permission → absent after sync = no access). Never leaves
  // a hung loader: the timeout safety net guarantees it.
  const countResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(openCount()),
    isPending: () => counts.isLoading,
  });

  async function handleCreate(e: Event) {
    e.preventDefault();
    const d = drizzle();
    if (!d) {
      return;
    }
    // Location is optional on create (same as vendor in po / origin in
    // picking): the draft is created anyway and completed later. The mutation
    // already accepts `locationId: null` — blocking here on the client left the
    // "Start count" button with no visible effect when no location was
    // selected.
    const id = await createInventoryCountMutation(d, orgId(), {
      locationId: locationId() || null,
      reference: reference().trim() || null,
    });
    setReference("");
    setOpenCountId(id);
  }

  async function handleAddLine() {
    const d = drizzle();
    const count = openCount();
    const qty = parseQtyInput(lineQty());
    if (!d || !count || !lineProduct() || qty === null || qty < 0) {
      return;
    }
    // Tracked products require a lot.
    const lotId = productTracked() ? lineLot() || null : null;
    if (productTracked() && !lotId) {
      return;
    }
    await addInventoryCountLineMutation(
      d,
      orgId(),
      { id: count.id, location_id: count.location_id },
      lineProduct(),
      qty,
      lotId,
    );
    setLineProduct("");
    setLineLot("");
    setLineQty("");
  }

  async function handleValidate() {
    const d = drizzle();
    const count = openCount();
    if (!d || !count) {
      return;
    }
    const lines = openLines.data ?? [];
    // CC-7: blocking validation (await). `validateInventoryCountMutation`
    // may THROW (verified phantom UPDATE, see
    // `@stockpilot/shared/inventory/mutations`): without this catch, a rejection
    // would surface as an unhandled rejection instead of notifying the user.
    try {
      await validateInventoryCountMutation(
        d,
        orgId(),
        { id: count.id, state: count.state, location_id: count.location_id },
        lines.map((l) => ({
          product_id: l.product_id,
          lot_id: l.lot_id,
          theoretical_qty: l.theoretical_qty,
          counted_qty: l.counted_qty,
        })),
      );
    } catch {
      showToast(t(msg`Could not validate the count`), "error");
    }
  }

  async function handleDeleteLine(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deleteInventoryCountLineMutation(d, id);
  }

  async function handleDeleteCount(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deleteInventoryCountMutation(d, id);
    if (openCountId() === id) {
      setOpenCountId(null);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Cycle counts`)}
        description={t(msg`Count stock by location and post the adjustment differences.`)}
        icon={ClipboardCheck}
      />

      <form
        class="mt-6 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreate}
        data-testid="count-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Location`)}</label>
          <Select
            class="w-44"
            value={locationId()}
            onChange={(v) => setLocationId(v)}
            data-testid="count-location-select"
            aria-label={t(msg`Location`)}
            placeholder={t(msg`Select location`)}
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Reference`)}</label>
          <input
            class="w-40 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder={t(msg`Optional`)}
            value={reference()}
            onInput={(e) => setReference(e.currentTarget.value)}
            data-testid="count-reference-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="count-create-button">
          {t(msg`Start count`)}
        </Button>
      </form>

      {/* Unresolvable deep-link: URL requests a count that does not exist / no access
          / offline. We show feedback instead of a silent empty panel. */}
      <Show
        when={openCountId() && countResolution() !== "ready" && countResolution() !== "loading"}
      >
        <div class="mt-6" data-testid="count-not-found">
          <NotFoundState
            variant={countResolution() === "offline" ? "offline" : "not-found"}
            title={countResolution() === "offline" ? undefined : t(msg`Count not found`)}
            description={
              countResolution() === "offline"
                ? undefined
                : t(msg`This count may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to counts`),
              onClick: () => {
                setOpenCountId(null);
              },
            }}
          />
        </div>
      </Show>

      <Show when={openCount()}>
        {(count) => (
          <div
            class="mt-6 rounded-md border border-border p-4"
            data-testid="count-editor"
            data-count-id={count().id}
            data-count-state={count().state ?? ""}
          >
            <div class="flex items-center justify-between">
              <h2 class="text-lg font-semibold">
                {t(msg`Count`)} · {locationName()(count().location_id ?? "")}
                <Show when={count().reference}>
                  {" "}
                  <span class="text-muted-foreground">#{count().reference}</span>
                </Show>
              </h2>
              <span
                class="rounded-full border border-border px-2 py-0.5 text-xs"
                data-testid="count-editor-state"
              >
                {count().state === "done" ? t(msg`Validated`) : t(msg`Draft`)}
              </span>
            </div>

            <Show when={count().state !== "done"}>
              <div class="mt-4 flex flex-wrap items-end gap-3">
                <div class="flex flex-col gap-1">
                  <label class="text-xs text-muted-foreground">{t(msg`Product`)}</label>
                  <Select
                    class="w-44"
                    value={lineProduct()}
                    onChange={(v) => {
                      setLineProduct(v);
                      setLineLot("");
                    }}
                    data-testid="count-line-product-select"
                    aria-label={t(msg`Product`)}
                    placeholder={t(msg`Select product`)}
                    options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                  />
                </div>
                <Show when={productTracked()}>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs text-muted-foreground">{t(msg`Lot / serial`)}</label>
                    <Select
                      class="w-40"
                      value={lineLot()}
                      onChange={(v) => setLineLot(v)}
                      data-testid="count-line-lot-select"
                      aria-label={t(msg`Lot / serial`)}
                      placeholder={t(msg`Select lot`)}
                      options={(lineLots.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
                    />
                  </div>
                </Show>
                <div class="flex flex-col gap-1">
                  <label class="text-xs text-muted-foreground">{t(msg`Counted quantity`)}</label>
                  <input
                    class="w-28 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
                    placeholder="0"
                    inputmode="decimal"
                    value={lineQty()}
                    onInput={(e) => setLineQty(e.currentTarget.value)}
                    data-testid="count-line-qty-input"
                  />
                </div>
                <Button
                  onClick={() => void handleAddLine()}
                  disabled={!drizzle()}
                  data-testid="count-line-add-button"
                >
                  {t(msg`Add line`)}
                </Button>
              </div>
            </Show>

            <div class="mt-4" data-testid="count-line-list">
              <Show
                when={(openLines.data ?? []).length > 0}
                fallback={<p class="text-sm text-muted-foreground">{t(msg`No lines yet.`)}</p>}
              >
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-border text-left text-muted-foreground">
                      <th class="py-2 font-medium">{t(msg`Product`)}</th>
                      <th class="py-2 font-medium text-right">{t(msg`Theoretical`)}</th>
                      <th class="py-2 font-medium text-right">{t(msg`Counted`)}</th>
                      <th class="py-2 font-medium text-right">{t(msg`Difference`)}</th>
                      <th class="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    <For each={openLines.data ?? []}>
                      {(line) => {
                        const diff = (line.counted_qty ?? 0) - (line.theoretical_qty ?? 0);
                        return (
                          <SwipeTableRow
                            class="border-b border-border/50"
                            data-testid="count-line-row"
                            disabled={count().state === "done"}
                            left={
                              count().state === "done"
                                ? undefined
                                : {
                                    icon: Trash2,
                                    label: t(msg`Remove`),
                                    tone: "danger",
                                    exits: true,
                                    onCommit: () => void handleDeleteLine(line.id),
                                  }
                            }
                          >
                            <td class="py-2">
                              {productName()(line.product_id ?? "")}
                              <Show when={line.lot_id}>
                                {" "}
                                <span class="text-xs text-muted-foreground">
                                  ({lotName()(line.lot_id ?? "")})
                                </span>
                              </Show>
                            </td>
                            <td class="py-2 text-right tabular-nums">
                              {fromMinorQty(line.theoretical_qty ?? 0)}
                            </td>
                            <td
                              class="py-2 text-right tabular-nums"
                              data-testid="count-line-counted"
                            >
                              {fromMinorQty(line.counted_qty ?? 0)}
                            </td>
                            <td class="py-2 text-right tabular-nums" data-testid="count-line-diff">
                              {fromMinorQty(diff)}
                            </td>
                            <td class="py-2 text-right">
                              <Show when={count().state !== "done"}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => void handleDeleteLine(line.id)}
                                  data-testid="count-line-delete-button"
                                >
                                  {t(msg`Remove`)}
                                </Button>
                              </Show>
                            </td>
                          </SwipeTableRow>
                        );
                      }}
                    </For>
                  </tbody>
                </table>
              </Show>
            </div>

            <Show when={count().state !== "done"}>
              <div class="mt-4 flex gap-2">
                <Button
                  onClick={() => void handleValidate()}
                  disabled={!drizzle()}
                  data-testid="count-validate-button"
                >
                  {t(msg`Validate count`)}
                </Button>
                <Button variant="ghost" onClick={() => setOpenCountId(null)}>
                  {t(msg`Close`)}
                </Button>
              </div>
            </Show>
          </div>
        )}
      </Show>

      <h2 class="mt-8 text-lg font-semibold">{t(msg`Recent counts`)}</h2>
      <div class="mt-3" data-testid="count-list">
        <Show
          when={(counts.data ?? []).length > 0}
          fallback={
            <EmptyState
              icon={ClipboardCheck}
              illustration={EmptyOrdersIllustration}
              title={t(msg`No counts yet`)}
              description={t(msg`Start a count to reconcile your on-hand quantities.`)}
            />
          }
        >
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={counts.data ?? []}>
              {(c) => (
                <li data-testid="count-row" data-count-id={c.id} data-count-state={c.state ?? ""}>
                  <SwipeRow
                    right={{
                      icon: FolderOpen,
                      label: t(msg`Open`),
                      tone: "primary",
                      onCommit: () => setOpenCountId(c.id),
                    }}
                    left={
                      c.state === "done"
                        ? undefined
                        : {
                            icon: Trash2,
                            label: t(msg`Delete`),
                            tone: "danger",
                            exits: true,
                            onCommit: () => void handleDeleteCount(c.id),
                          }
                    }
                  >
                    <div class="flex items-center gap-3 px-4 py-3">
                      <ClipboardCheck class="h-5 w-5 text-primary shrink-0" />
                      <div class="flex-1 min-w-0">
                        <p class="font-medium">
                          {locationName()(c.location_id ?? "")}
                          <Show when={c.reference}>
                            {" "}
                            <span class="text-muted-foreground">#{c.reference}</span>
                          </Show>
                        </p>
                      </div>
                      <span
                        class="rounded-full border border-border px-2 py-0.5 text-xs"
                        data-testid="count-row-state"
                      >
                        {c.state === "done" ? t(msg`Validated`) : t(msg`Draft`)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpenCountId(c.id)}
                        data-testid="count-open-button"
                      >
                        {t(msg`Open`)}
                      </Button>
                      <Show when={c.state !== "done"}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleDeleteCount(c.id)}
                          data-testid="count-delete-button"
                        >
                          {t(msg`Delete`)}
                        </Button>
                      </Show>
                    </div>
                  </SwipeRow>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </PageContainer>
  );
}

export const countsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/counts",
  validateSearch: (search: Record<string, unknown>): { count?: string } => ({
    count:
      typeof search.count === "string" && search.count.trim() !== "" ? search.count : undefined,
  }),
  component: CountsPage,
});
